import { useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "@/i18n";

export const PinSetupModal = () => {
  const { t } = useTranslation();
  const { setPin, skipPinSetup } = useAuth();
  const [pin, setLocalPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const handleSetPin = async () => {
    if (pin !== confirmPin) {
      toast.error(t("common.pinsNoMatch"));
      setStep(1);
      setLocalPin("");
      setConfirmPin("");
      return;
    }
    setSubmitting(true);
    try {
      await setPin(pin);
      localStorage.setItem("hasPinEnabled", "true");
      toast.success(t("common.pinSet"));
    } catch (e) {
      toast.error(t("common.pinFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content text-center">
        <Lock size={40} className="mx-auto mb-4 text-gray-400" />
        <h3 className="text-xl font-semibold mb-2">{t('modals.setupPin')}</h3>
        <p className="text-gray-500 text-sm mb-6">
          {step === 1 ? t('settings.createPinHint') : t('settings.confirmPin')}
        </p>
        
        <div className="flex justify-center gap-3 mb-6">
          {[0, 1, 2, 3].map((i) => (
            <input
              key={i}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={step === 1 ? (pin[i] || "") : (confirmPin[i] || "")}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                if (step === 1) {
                  const newPin = pin.split("");
                  newPin[i] = val;
                  setLocalPin(newPin.join(""));
                } else {
                  const newPin = confirmPin.split("");
                  newPin[i] = val;
                  setConfirmPin(newPin.join(""));
                }
                if (val && e.target.nextElementSibling) {
                  e.target.nextElementSibling.focus();
                }
              }}
              className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none"
            />
          ))}
        </div>

        {step === 1 ? (
          <button 
            onClick={() => { if (pin.length === 4) setStep(2); }}
            className="clean-btn-primary w-full mb-3"
            disabled={pin.length !== 4}
          >
            {t('modals.continueBtn')}
          </button>
        ) : (
          <button 
            onClick={handleSetPin}
            className="clean-btn-primary w-full mb-3"
            disabled={confirmPin.length !== 4 || submitting}
          >
            {submitting ? t('settings.settingUpPin') : t('settings.setPinTitle')}
          </button>
        )}
        
        <button onClick={skipPinSetup} className="text-gray-500 hover:text-black text-sm">
          {t('modals.skipForNow')}
        </button>
      </div>
    </div>
  );
};
